import { escapeHtml, todayInTimezone } from './utils.js';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function wrap(className, inner) {
	const t = ['d', 'i', 'v'].join('');
	return '<' + t + ' class="' + className + '">' + inner + '</' + t + '>';
}

/**
 * Month calendar (Mon–Sun); calls onSelect(dateStr) with YYYY-MM-DD.
 * Optional onMonthChange(year, month) when the visible month changes.
 */
export function createCalendar(container, options) {
	const { onSelect, isDateDisabled, selectedDate, timezone, onMonthChange } = options;
	let viewYear;
	let viewMonth;

	function pad(n) {
		return n < 10 ? '0' + n : String(n);
	}

	function toDateStr(y, m, d) {
		return y + '-' + pad(m + 1) + '-' + pad(d);
	}

	function todayStr() {
		if (timezone) {
			return todayInTimezone(timezone);
		}
		const t = new Date();
		return toDateStr(t.getFullYear(), t.getMonth(), t.getDate());
	}

	function mondayIndex(date) {
		const d = date.getDay();
		return d === 0 ? 6 : d - 1;
	}

	function notifyMonth() {
		if (typeof onMonthChange === 'function') {
			onMonthChange(viewYear, viewMonth + 1);
		}
	}

	function render() {
		const first = new Date(viewYear, viewMonth, 1);
		const startPad = mondayIndex(first);
		const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
		const monthLabel = first.toLocaleDateString(undefined, {
			month: 'long',
			year: 'numeric',
		});
		const today = todayStr();

		let cells = '';
		for (let i = 0; i < startPad; i++) {
			cells +=
				'<button type="button" class="br-cal-day br-cal-day--empty" disabled aria-hidden="true"></button>';
		}
		for (let day = 1; day <= daysInMonth; day++) {
			const dateStr = toDateStr(viewYear, viewMonth, day);
			const disabled = isDateDisabled ? isDateDisabled(dateStr) : false;
			const selected = selectedDate === dateStr;
			const isToday = dateStr === today;
			let cls = 'br-cal-day';
			if (selected) {
				cls += ' is-selected';
			}
			if (disabled) {
				cls += ' is-disabled is-unavailable';
			}
			if (isToday) {
				cls += ' is-today';
			}
			cells +=
				'<button type="button" class="' +
				cls +
				'" data-date="' +
				escapeHtml(dateStr) +
				'"' +
				(disabled ? ' disabled' : '') +
				'>' +
				day +
				'</button>';
		}

		const header =
			'<button type="button" class="br-cal__nav" data-nav="prev" aria-label="Previous month">← Prev</button>' +
			'<span class="br-cal__title">' +
			escapeHtml(monthLabel) +
			'</span>' +
			'<button type="button" class="br-cal__nav" data-nav="next" aria-label="Next month">Next →</button>';

		container.innerHTML =
			wrap(
				'br-cal',
				wrap('br-cal__header', header) +
					wrap(
						'br-cal__weekdays',
						WEEKDAYS.map((d) => '<span>' + d + '</span>').join('')
					) +
					wrap('br-cal__grid', cells)
			);

		const minView = todayStr().slice(0, 7);

		container.querySelectorAll('[data-nav]').forEach((btn) => {
			btn.addEventListener('click', () => {
				if (btn.getAttribute('data-nav') === 'prev') {
					viewMonth -= 1;
					if (viewMonth < 0) {
						viewMonth = 11;
						viewYear -= 1;
					}
					const viewKey = viewYear + '-' + pad(viewMonth + 1);
					if (viewKey < minView) {
						viewMonth += 1;
						if (viewMonth > 11) {
							viewMonth = 0;
							viewYear += 1;
						}
						return;
					}
				} else {
					viewMonth += 1;
					if (viewMonth > 11) {
						viewMonth = 0;
						viewYear += 1;
					}
				}
				notifyMonth();
				render();
			});
		});

		container.querySelectorAll('.br-cal-day[data-date]').forEach((btn) => {
			btn.addEventListener('click', () => {
				if (btn.disabled) {
					return;
				}
				onSelect(btn.getAttribute('data-date'));
			});
		});
	}

	const now = new Date();
	viewYear = now.getFullYear();
	viewMonth = now.getMonth();
	notifyMonth();
	render();

	return {
		setSelected(dateStr) {
			options.selectedDate = dateStr;
			render();
		},
		refresh() {
			render();
		},
		getViewMonth() {
			return { year: viewYear, month: viewMonth + 1 };
		},
	};
}
